import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getConfig } from 'utils/helperMethods';
import { PricingReport } from 'types/pricingSimulator';
import { FilterContextData } from 'contexts/FilterContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingAnalyticsUrls } from 'config/urls';
import { changeAnalyticsChartParser } from 'parser/pricingAnalytics/changeAnalyticsParser';
import { ConfigKeys } from 'utils/Enums';
import ChangeAnalyticsChartItem from './ChangeAnalyticsChartItem';

const mainHeader = 'Net Change';
const containerId = 'change-analytics-chart-container';
const ChangeAnalyticsContainer: FC = () => {
    const containerWidth = useContainerWidth({
        containerId: containerId,
        buffer: yAxisGraphWidth + 34 * 2 + 112,
    }); //subtracting graph width and padding on 2 sides

    //inf scroller width ref
    const infScrollerWidthRef = useRef<any[]>([0, 0]);

    //filter context
    const enabledVariable = true;

    const {
        selectedReportInfo,
        updateReportProperty,
        filters,
        isUpdate,
        loggedInUser,
        setSelectedReportInfo,
        showPercentageChange,
    } = useContext(PricingAnalyticsDataContext);
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const analyticsChartLimit = pricingSimulatorConfig.analyticsChartLimit;
    const enableOneChangeAnalyticsChart = pricingSimulatorConfig?.enableOneChangeAnalyticsChart || false;
    const [commonVariables, setCommonVariables] = useState<any[] | undefined>();

    //set variables
    useEffect(() => {
        //reset inf scroller width
        infScrollerWidthRef.current = [0, 0];
        const variables = selectedReportInfo?.changeAnalytics?.input?.map((graphItem) => {
            return {
                kpi_id: graphItem?.kpiId,
                graph_id: graphItem?.graphId,
                attribute_type: graphItem?.attributeType?.value,
                user_name: loggedInUser,
                base_scenario_id: selectedReportInfo?.baseScenario?.scenarioId,
                report_id: selectedReportInfo?.reportId,
                filters,
                is_update: isUpdate,
                limit: analyticsChartLimit,
                offset: 0,
                show_percent: showPercentageChange,
            };
        });
        setCommonVariables(variables);
    }, [
        loggedInUser,
        filters,
        selectedReportInfo?.baseScenario?.scenarioId,
        selectedReportInfo?.changeAnalytics?.input,
        showPercentageChange,
    ]);

    //chart 1
    const { loading: chart1Loading, error: chart1Error } = useCommonApiHandler({
        method: 'post',
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariables?.[0]?.kpi_id) },
        url: pricingAnalyticsUrls.getChangeAnalyticsDataUrl,
        queryKey: ['pa-change-analytics-chart', 'pa-change-analytics-chart-1', commonVariables?.[0]],
        variables: { ...(commonVariables?.[0] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = changeAnalyticsChartParser(
                Number(commonVariables?.[0]?.graph_id),
                res?.total_count,
                res?.data,
            );

            //if inf scroller , then merge data
            if (commonVariables?.[0]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.changeAnalytics?.output?.[0]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.changeAnalytics?.output?.[0]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.changeAnalytics?.output?.[0]?.data ?? []),
                    ...outputObj?.data,
                ];
            }
            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    changeAnalytics: {
                        input: prevState?.changeAnalytics?.input,
                        output: [outputObj, prevState?.changeAnalytics?.output?.[1]],
                    },
                };
            });
            return {};
        },
    });

    //chart 2
    const { loading: chart2Loading, error: chart2Error } = useCommonApiHandler({
        method: 'post',
        url: pricingAnalyticsUrls.getChangeAnalyticsDataUrl,
        queryConfig: { enabled: Boolean(enabledVariable) && Boolean(commonVariables?.[1]?.kpi_id) },
        queryKey: ['pa-change-analytics-chart', 'pa-change-analytics-chart-2', commonVariables?.[1]],
        variables: { ...(commonVariables?.[1] || {}) },
        parserFunction: (res: any) => {
            if (res?.error || !res?.data?.length) {
                throw 'No Data';
            }
            const outputObj = changeAnalyticsChartParser(
                Number(commonVariables?.[1]?.graph_id),
                res?.total_count,
                res?.data,
            );

            //if inf scroller , then merge data
            if (commonVariables?.[1]?.offset > 0) {
                outputObj.maxVal = Math.max(
                    selectedReportInfo?.changeAnalytics?.output?.[1]?.maxVal ?? 0,
                    outputObj.maxVal ?? 0,
                );
                outputObj.minVal = Math.min(
                    selectedReportInfo?.changeAnalytics?.output?.[1]?.minVal ?? 0,
                    outputObj.minVal ?? 0,
                );
                outputObj.data = [
                    ...(selectedReportInfo?.changeAnalytics?.output?.[1]?.data ?? []),
                    ...outputObj?.data,
                ];
            }

            setSelectedReportInfo((prevState: any): PricingReport => {
                return {
                    ...prevState,
                    changeAnalytics: {
                        input: prevState?.changeAnalytics?.input,
                        output: [prevState?.changeAnalytics?.output?.[0], outputObj],
                    },
                };
            });
            return {};
        },
    });

    const indexLoaderErrorMapping: any = {
        0: [chart1Loading, chart1Error],
        1: [chart2Loading, chart2Error],
    };

    const infiniteScrollOnNext = useCallback(
        (width: number, loading: boolean, index: number) => {
            if (!loading) {
                const newOffset = Number(commonVariables?.[index]?.offset) + analyticsChartLimit;

                //updating width
                infScrollerWidthRef.current.splice(index, 1, width);

                //updating offset
                setCommonVariables((prevValue: any) => {
                    return prevValue?.map((variable: any, idx: number) => {
                        return {
                            ...variable,
                            offset: index === idx ? newOffset : variable?.offset,
                        };
                    });
                });
            }
        },
        [commonVariables, setCommonVariables],
    );

    const selectedLensItem = useMemo(() => {
        const names: any[] = [];
        selectedReportInfo?.changeAnalytics?.input?.forEach((item, index) => {
            item?.lensConfig?.forEach((lens) => {
                lens?.attributes?.forEach((kpi) => {
                    if (kpi.isSelected && !names?.[index]) {
                        names.push(kpi);
                        return;
                    }
                    if (names?.[index]) return;
                });
                if (names?.[index]) return;
            });
            if (names?.[index]) return;
        });
        return names;
    }, [selectedReportInfo?.changeAnalytics?.input]);

    return (
        <div
            id={containerId}
            className={`w-full grid  gap-4 mt-4 ${enableOneChangeAnalyticsChart ? 'grid-cols-1' : ' grid-cols-2'}`}
        >
            {selectedReportInfo?.changeAnalytics?.output
                ?.filter((item, index) => (enableOneChangeAnalyticsChart ? index == 0 : true))
                .map((dataItem, index) => (
                    <ChangeAnalyticsChartItem
                        key={dataItem?.id}
                        dataItem={dataItem}
                        index={index}
                        containerWidth={containerWidth}
                        infScrollerWidthRef={infScrollerWidthRef}
                        infiniteScrollOnNext={infiniteScrollOnNext}
                        indexLoaderErrorMapping={indexLoaderErrorMapping}
                        selectedLensItem={selectedLensItem}
                        selectedReportInfo={selectedReportInfo}
                        updateReportProperty={updateReportProperty}
                        mainHeader={mainHeader}
                        enableOneChangeAnalyticsChart={enableOneChangeAnalyticsChart}
                    />
                ))}
        </div>
    );
};

export default ChangeAnalyticsContainer;
